import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

export interface DebugWindowState {
  x: number;
  y: number;
  visible: boolean;
}

@customElement('himum-debug-window')
export class HimumDebugWindow extends LitElement {
  static styles = css`
    :host {
      position: absolute;
      display: block;
      pointer-events: auto;
      user-select: none;
      font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
      color: #eee;
    }

    :host([docked]) {
      position: relative;
      transform: none !important;
      width: 100%;
      height: 100%;
      min-height: 0;
    }

    :host([docked]) .win {
      resize: none;
      min-width: 0;
      min-height: 0;
      height: 100%;
    }

    :host([docked]) .resize-handle {
      display: none;
    }

    .win {
      border: 1px solid rgba(255, 255, 255, 0.2);
      background: rgba(0, 0, 0, 0.85);
      backdrop-filter: blur(6px);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      min-width: 260px;
      min-height: 140px;
      height: 100%;
      position: relative;
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 10px;
      cursor: grab;
      border-bottom: 1px solid rgba(255, 255, 255, 0.15);
      background: rgba(40, 40, 50, 0.9);
    }

    .header:active {
      cursor: grabbing;
    }

    .header::before {
      content: '⋮⋮';
      margin-right: 8px;
      opacity: 0.4;
      font-size: 10px;
      letter-spacing: 2px;
    }

    :host([docked]) .header {
      cursor: default;
    }

    :host([docked]) .header::before {
      display: none;
    }

    .title {
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.2px;
      flex: 1;
    }

    .content {
      padding: 10px;
      user-select: text;
      overflow: auto;
      flex: 1;
      min-height: 0;
    }

    button {
      font: inherit;
      font-size: 12px;
      padding: 4px 8px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      background: rgba(255, 255, 255, 0.08);
      color: #eee;
      cursor: pointer;
    }

    button:hover {
      background: rgba(255, 255, 255, 0.12);
    }

    .close-btn {
      padding: 2px 8px;
      margin-left: 8px;
      font-size: 16px;
      line-height: 1;
      color: #999;
    }

    .close-btn:hover {
      background: rgba(255, 80, 80, 0.6);
      color: #fff;
    }

    /* Resize handle - bottom-right corner */
    .resize-handle {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 16px;
      height: 16px;
      cursor: nwse-resize;
      background: linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.3) 50%);
      opacity: 0.6;
      transition: opacity 0.2s;
    }

    .resize-handle:hover {
      opacity: 1;
    }

    /* Resize edges */
    .resize-edge-right {
      position: absolute;
      top: 30px;
      right: -3px;
      width: 6px;
      bottom: 16px;
      cursor: ew-resize;
    }

    .resize-edge-bottom {
      position: absolute;
      bottom: -3px;
      left: 0;
      right: 16px;
      height: 6px;
      cursor: ns-resize;
    }
  `;

  @property({ type: String }) windowId = '';
  @property({ type: String }) title = '';
  @property({ type: Number }) initialX = 20;
  @property({ type: Number }) initialY = 20;
  @property({ type: Number }) initialWidth = 420;
  @property({ type: Number }) initialHeight = 260;
  @property({ type: Boolean }) visible = true;
  @property({ type: Boolean, reflect: true }) docked = false;

  private x = 20;
  private y = 20;
  private width = 420;
  private height = 260;
  private resizeObserver: ResizeObserver | null = null;

  private drag:
    | {
        pointerId: number;
        startX: number;
        startY: number;
        startWinX: number;
        startWinY: number;
      }
    | null = null;

  private resize:
    | {
        pointerId: number;
        startX: number;
        startY: number;
        startWidth: number;
        startHeight: number;
        edge: 'corner' | 'right' | 'bottom';
      }
    | null = null;

  private initialized = false;

  updated(): void {
    // Initialize position/size from initial props on first render
    if (!this.initialized) {
      this.x = this.initialX;
      this.y = this.initialY;
      this.width = this.initialWidth;
      this.height = this.initialHeight;
      this.initialized = true;
    }

    this.style.display = this.visible ? 'block' : 'none';

    if (this.docked) {
      // Clear any inline styles that may have been set before docking.
      this.style.transform = '';
      this.style.width = '';
      this.style.height = '';
    } else {
      this.style.transform = `translate(${this.x}px, ${this.y}px)`;
      this.style.width = `${this.width}px`;
      this.style.height = `${this.height}px`;
    }

    const win = this.renderRoot.querySelector('.win') as HTMLElement | null;
    if (win && !this.resizeObserver && !this.docked) {
      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const cr = entry.contentRect;
        const nextW = Math.round(cr.width);
        const nextH = Math.round(cr.height);
        if (nextW === this.width && nextH === this.height) return;
        this.width = nextW;
        this.height = nextH;
        this.dispatchEvent(
          new CustomEvent('window-resize', {
            detail: { width: this.width, height: this.height },
            bubbles: true,
            composed: true,
          })
        );
      });
      this.resizeObserver.observe(win);
    }
  }

  disconnectedCallback(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    super.disconnectedCallback();
  }

  private onHeaderPointerDown(e: PointerEvent) {
    if (this.docked) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    this.drag = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startWinX: this.x,
      startWinY: this.y,
    };
    
    // Emit drag-start event for dock detection
    this.dispatchEvent(
      new CustomEvent('drag-start', {
        bubbles: true,
        composed: true,
      })
    );
  }

  private onHeaderPointerMove(e: PointerEvent) {
    if (this.docked) return;
    if (!this.drag || this.drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - this.drag.startX;
    const dy = e.clientY - this.drag.startY;
    this.x = Math.round(this.drag.startWinX + dx);
    this.y = Math.round(this.drag.startWinY + dy);
    
    // Directly update transform for immediate visual feedback
    this.style.transform = `translate(${this.x}px, ${this.y}px)`;
    
    this.dispatchEvent(
      new CustomEvent('window-move', {
        detail: { x: this.x, y: this.y },
        bubbles: true,
        composed: true,
      })
    );
  }

  private onHeaderPointerUp(e: PointerEvent) {
    if (this.docked) return;
    if (!this.drag || this.drag.pointerId !== e.pointerId) return;
    this.drag = null;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);

    // Signal that the drag ended so the HUD can dock if over a region.
    this.dispatchEvent(
      new CustomEvent('drag-end', {
        detail: { x: e.clientX, y: e.clientY },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleClose(e: Event) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('window-close', {
        bubbles: true,
        composed: true,
      })
    );
  }

  private onResizePointerDown(e: PointerEvent, edge: 'corner' | 'right' | 'bottom') {
    if (this.docked) return;
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    this.resize = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: this.width,
      startHeight: this.height,
      edge,
    };
  }

  private onResizePointerMove(e: PointerEvent) {
    if (this.docked) return;
    if (!this.resize || this.resize.pointerId !== e.pointerId) return;
    
    const dx = e.clientX - this.resize.startX;
    const dy = e.clientY - this.resize.startY;
    
    const minWidth = 260;
    const minHeight = 140;

    if (this.resize.edge === 'corner' || this.resize.edge === 'right') {
      this.width = Math.max(minWidth, Math.round(this.resize.startWidth + dx));
    }
    if (this.resize.edge === 'corner' || this.resize.edge === 'bottom') {
      this.height = Math.max(minHeight, Math.round(this.resize.startHeight + dy));
    }

    // Directly update size for immediate visual feedback
    this.style.width = `${this.width}px`;
    this.style.height = `${this.height}px`;
  }

  private onResizePointerUp(e: PointerEvent) {
    if (this.docked) return;
    if (!this.resize || this.resize.pointerId !== e.pointerId) return;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    this.resize = null;

    this.dispatchEvent(
      new CustomEvent('window-resize', {
        detail: { width: this.width, height: this.height },
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    return html`
      <div class="win">
        <div
          class="header"
          @pointerdown=${this.onHeaderPointerDown}
          @pointermove=${this.onHeaderPointerMove}
          @pointerup=${this.onHeaderPointerUp}
        >
          <div class="title">${this.title}</div>
          <div>
            <slot name="header-actions"></slot>
            <button class="close-btn" @click=${this.handleClose} title="Close">×</button>
          </div>
        </div>
        <div class="content"><slot></slot></div>
        ${!this.docked
          ? html`
              <div
                class="resize-edge-right"
                @pointerdown=${(e: PointerEvent) => this.onResizePointerDown(e, 'right')}
                @pointermove=${this.onResizePointerMove}
                @pointerup=${this.onResizePointerUp}
              ></div>
              <div
                class="resize-edge-bottom"
                @pointerdown=${(e: PointerEvent) => this.onResizePointerDown(e, 'bottom')}
                @pointermove=${this.onResizePointerMove}
                @pointerup=${this.onResizePointerUp}
              ></div>
              <div
                class="resize-handle"
                @pointerdown=${(e: PointerEvent) => this.onResizePointerDown(e, 'corner')}
                @pointermove=${this.onResizePointerMove}
                @pointerup=${this.onResizePointerUp}
              ></div>
            `
          : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'himum-debug-window': HimumDebugWindow;
  }
}
